import {
  AfterViewInit,
  Directive,
  ElementRef,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  PLATFORM_ID,
  SimpleChanges,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

/**
 * Mounts a <video>/<audio> element's `src` only while it is near the viewport,
 * and clears it (pause + removeAttribute + load) once it scrolls away.
 *
 * Feeds that render one media element per item (posts, blogs, chat) keep
 * appending items as the user scrolls, so without this every mounted element
 * holds a real decoder — Chrome caps concurrent WebMediaPlayers (~75) and
 * starts refusing to create new ones, which is why videos stop playing after
 * a long scroll session.
 */
@Directive({
  selector: 'video[appLazyMedia], audio[appLazyMedia]',
  standalone: true,
})
export class LazyMediaDirective implements AfterViewInit, OnChanges, OnDestroy {
  @Input('appLazyMedia') src = '';

  private observer?: IntersectionObserver;
  private mounted = false;
  private unmountTimer?: ReturnType<typeof setTimeout>;
  private readonly isBrowser: boolean;
  /** Debounce for leaving the viewport — avoids tearing down (pause + strip
   *  src + reload) a video that only *briefly* reports non-intersecting
   *  because of layout churn elsewhere on the page. Without this, a video
   *  can get stuck in a mount → unmount → mount loop and never finish
   *  loading a visible frame. */
  private static readonly UNMOUNT_DEBOUNCE_MS = 400;

  constructor(
    private readonly el: ElementRef<HTMLVideoElement | HTMLAudioElement>,
    @Inject(PLATFORM_ID) platformId: object,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngAfterViewInit(): void {
    if (!this.isBrowser) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            if (this.unmountTimer) {
              clearTimeout(this.unmountTimer);
              this.unmountTimer = undefined;
            }
            this.mount();
          } else {
            this.scheduleUnmount();
          }
        }
      },
      { rootMargin: '600px 0px' },
    );
    this.observer.observe(this.el.nativeElement);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Item recycled onto an already-mounted element (e.g. trackBy reuse) — swap the source.
    if (changes['src'] && !changes['src'].firstChange && this.mounted) {
      this.mount();
    }
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    if (this.unmountTimer) clearTimeout(this.unmountTimer);
    this.unmount();
  }

  private scheduleUnmount(): void {
    if (this.unmountTimer) return;
    this.unmountTimer = setTimeout(() => {
      this.unmountTimer = undefined;
      this.unmount();
    }, LazyMediaDirective.UNMOUNT_DEBOUNCE_MS);
  }

  private mount(): void {
    const media = this.el.nativeElement;
    if (this.src && media.getAttribute('src') !== this.src) {
      media.setAttribute('src', this.src);
      media.load();
    }
    this.mounted = true;
  }

  private unmount(): void {
    const media = this.el.nativeElement;
    if (media.hasAttribute('src')) {
      media.pause();
      media.removeAttribute('src');
      media.load();
    }
    this.mounted = false;
  }
}